import { createReducer } from '@reduxjs/toolkit'

import { TRefBlockWidget, TReport, TReportEdition } from 'types/GlobalReport'

import * as actions from '../../actions/Reports/actions'
import * as editionActions from '../../actions/ReportEditions/actions'
import * as widgetActions from '../../actions/Widgets/ReportWidgets/actions'
import * as authenticationActions from '../../actions/Authentication/actions'

import { insertItems, removeItem, upsertItem, upsertItems } from 'redux/utils'

export interface ReportReducerType {
  editions: {
    [id: string]: TReportEdition
  }
  reports: {
    [id: string]: TReport
  }

  // only used for reference on initial load
  widgets: {
    [id: string]: TRefBlockWidget
  }
}

// Initial state
const INITIAL_STATE: ReportReducerType = {
  editions: {},
  reports: {},
  widgets: {}
}

// Reducer
const reportReducer = createReducer(INITIAL_STATE, (builder) => {
  builder

    // tryGetAllReports
    .addCase(actions.getAllSuccess, (state, action) => {
      state.reports = insertItems(action.payload)

      action.payload
        .flatMap((report) => report.blocks)
        .flatMap((block) => block.widgets)
        .forEach((widget) => {
          state.widgets[widget.id] = widget
        })
    })

    // tryCreateReport
    .addCase(actions.createSuccess, (state, action) => {
      state.reports = upsertItem(state.reports, action.payload)
    })

    // tryCopyReport
    .addCase(actions.copyReportSuccess, (state, action) => {
      state.reports = upsertItem(state.reports, action.payload)

      action.payload.blocks
        .flatMap((block) => block.widgets)
        .forEach((widget) => {
          state.widgets[widget.id] = widget
        })
    })

    // tryDeleteReport
    .addCase(actions.destroySuccess, (state, action) => {
      state.reports = removeItem(state.reports, action.payload)
    })

    // tryUpdateReport
    .addCase(actions.updateSuccess, (state, action) => {
      state.reports = upsertItem(state.reports, action.payload)
    })

    // tryGetAllReportEditions
    .addCase(editionActions.getAll, (state) => state)
    .addCase(editionActions.getAllSuccess, (state, action) => {
      state.editions = upsertItems(state.editions, action.payload)

      action.payload
        .flatMap((edition) => edition.blocks)
        .flatMap((block) => block.widgets)
        .forEach((widget) => {
          state.widgets[widget.id] = widget
        })
    })
    .addCase(editionActions.getAllFailed, (state) => state)

    // tryCreateReportEdition
    .addCase(editionActions.create, (state) => state)
    .addCase(editionActions.createSuccess, (state, action) => {
      const edition = action.payload
      const report = state.reports[edition.report_id]

      state.editions[edition.id] = edition

      if (report) {
        report.editions = [...report.editions, edition.id]
      }

      edition.blocks
        .flatMap((block) => block.widgets)
        .forEach((widget) => {
          state.widgets[widget.id] = widget
        })
    })
    .addCase(editionActions.createFailed, (state) => state)

    // tryUpdateReportEdition
    .addCase(editionActions.update, (state) => state)
    .addCase(editionActions.updateSuccess, (state, action) => {
      const edition = action.payload

      state.editions[edition.id] = edition
    })
    .addCase(editionActions.updateFailed, (state) => state)

    // tryDeleteReportEdition
    .addCase(editionActions.destroy, (state) => state)
    .addCase(editionActions.destroySuccess, (state, action) => {
      const edition = state.editions[action.payload]

      if (edition) {
        const report = state.reports[edition.report_id]

        delete state.editions[edition.id]

        if (report) {
          report.editions = report.editions.filter((eid) => eid !== edition.id)
        }
      }
    })
    .addCase(editionActions.destroyFailed, (state) => state)

    // tryReorderBlocks
    .addCase(actions.reorderBlocksSuccess, (state, action) => {
      const { reportId, order } = action.payload
      const report = state.reports[reportId]

      if (report) {
        report.blocks.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
        report.blocks.forEach((block, i) => {
          block.index = i
        })
      }
    })

    // tryReorderBlockWidgets
    .addCase(actions.reorderBlockWidgetsSuccess, (state, action) => {
      const { reportId, blockId, order } = action.payload
      const report = state.reports[reportId]

      if (report) {
        const block = report.blocks.find((b) => b.id === blockId)

        if (block) {
          block.widgets.sort(
            (a, b) => order.indexOf(a.id) - order.indexOf(b.id)
          )
        }
      }
    })

    // tryPutReportFilters
    .addCase(actions.putFiltersSuccess, (state, action) => {
      const { reportId, filters } = action.payload
      const report = state.reports[reportId]

      if (report) {
        report.report_filters = filters
      }
    })

    // tryCreateReportWidget
    .addCase(actions.createWidgetSuccess, (state, action) => {
      const { widget, block } = action.payload

      const report = state.reports[block.report_id]

      if (!report.blocks.some((item) => item.id === block.id)) {
        // add if new block
        report.blocks.push(block)
      } else {
        const index = report.blocks.findIndex((item) => item.id === block.id)

        report.blocks[index] = block
      }

      state.widgets[widget.id] = widget
    })
    .addCase(widgetActions.destroySuccess, (state, action) => {
      const { id, reportId, deletedBlockId } = action.payload

      if (state.widgets[id]) delete state.widgets[id]

      const report = state.reports[reportId]

      if (report) {
        const blockIndex = report.blocks.findIndex((b) =>
          deletedBlockId
            ? b.id === deletedBlockId
            : b.widgets.find((w) => w.id === id)
        )

        if (blockIndex !== -1) {
          if (deletedBlockId) {
            report.blocks.splice(blockIndex, 1)
          } else {
            report.blocks[blockIndex].widgets = report.blocks[
              blockIndex
            ].widgets.filter((w) => w.id !== id)
          }
        }
      }
    })
    // tryUpdateReportWidget
    .addCase(widgetActions.updateSuccess, (state, action) => {
      const { payload } = action

      state.widgets[payload.data.id] = payload.data
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default reportReducer
